'use client';
import React, { useState, useRef, useLayoutEffect } from 'react';
import { Button } from '@ui';
import { IconChevronDown } from '@icons';
import { type AccordionItem } from './AccordionData';

export type { AccordionItem } from './AccordionData';

/** `accordion` (default): one panel at a time. `collapsible`: any number of panels open. */
export type AccordionType = 'accordion' | 'collapsible';

export interface AccordionProps {
  data: AccordionItem[];
  /** Root id (default `acc1`). */
  id?: string;
  type?: AccordionType;
  /** Extra classes on the root. Include `card` to apply card padding, border, and radius. */
  className?: string;
}

function initialOpenIndexes(data: AccordionItem[], type: AccordionType): number[] {
  const marked = data.reduce<number[]>((acc, item, i) => {
    if (item.defaultOpen) acc.push(i);
    return acc;
  }, []);
  if (type === 'collapsible') return marked;
  if (marked.length > 0) return [marked[0]];
  return data.length > 0 ? [0] : [];
}

function renderPanelContent(content: AccordionItem['content']) {
  if (typeof content === 'string' || typeof content === 'number') {
    return <p>{content}</p>;
  }
  return content;
}

const Accordion: React.FC<AccordionProps> = ({
  data,
  id = 'acc1',
  type = 'accordion',
  className = '',
}) => {
  const isCollapsible = type === 'collapsible';
  const [openIndexes, setOpenIndexes] = useState(() => initialOpenIndexes(data, type));
  const [prevIdx, setPrevIdx] = useState<number | null>(null);
  const panelRefs = useRef<(HTMLDivElement | null)[]>([]);

  useLayoutEffect(() => {
    if (!isCollapsible && prevIdx !== null && !openIndexes.includes(prevIdx)) {
      const prevPanel = panelRefs.current[prevIdx];
      if (prevPanel) {
        prevPanel.style.position = 'absolute';
        prevPanel.style.zIndex = '-1';
        prevPanel.style.pointerEvents = 'none';
        prevPanel.style.maxHeight = prevPanel.scrollHeight + 'px';
        void prevPanel.offsetHeight;
        prevPanel.style.maxHeight = '0px';
        setTimeout(() => {
          if (prevPanel) {
            prevPanel.style.position = '';
            prevPanel.style.zIndex = '';
            prevPanel.style.pointerEvents = '';
          }
        }, 400);
      }
    }
    openIndexes.forEach((idx) => {
      const panel = panelRefs.current[idx];
      if (panel) {
        panel.style.maxHeight = panel.scrollHeight + 'px';
      }
    });
  }, [openIndexes, prevIdx, isCollapsible]);

  const handleToggle = (idx: number) => {
    if (isCollapsible) {
      setOpenIndexes((prev) =>
        prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
      );
      return;
    }

    if (openIndexes.includes(idx)) {
      const panel = panelRefs.current[idx];
      if (panel) {
        panel.style.maxHeight = panel.scrollHeight + 'px';
        void panel.offsetHeight;
        panel.style.maxHeight = '0px';
        setTimeout(() => {
          setPrevIdx(idx);
          setOpenIndexes([]);
        }, 400);
      } else {
        setPrevIdx(idx);
        setOpenIndexes([]);
      }
    } else {
      setPrevIdx(openIndexes[0] ?? null);
      setOpenIndexes([idx]);
    }
  };

  return (
    <div
      className={['accordion', isCollapsible ? 'collapsible' : '', className]
        .filter(Boolean)
        .join(' ')}
      data-accordion={isCollapsible ? 'multiple' : 'single'}
      id={id}
      style={{ position: 'relative' }}
    >
      {data.map((item, idx) => {
        const isOpen = openIndexes.includes(idx);
        return (
          <div className={`card acc-item${isOpen ? ' is-open' : ''}`} key={item.id}>
            <h3 className='acc-header'>
              <Button
                className='acc-trigger'
                id={item.id}
                aria-expanded={isOpen}
                aria-controls={item.panelId}
                onClick={() => handleToggle(idx)}
                type='button'
                label={item.title}
                icon={
                  <span className='acc-indicator' aria-hidden>
                    <IconChevronDown />
                  </span>
                }
                iconPosition='left'
              />
            </h3>
            <div
              className='acc-panel'
              id={item.panelId}
              role='region'
              aria-labelledby={item.id}
              ref={(el) => {
                panelRefs.current[idx] = el;
              }}
              style={{
                overflow: 'hidden',
                transition: 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                maxHeight: isOpen
                  ? panelRefs.current[idx]?.scrollHeight
                    ? panelRefs.current[idx]!.scrollHeight + 'px'
                    : 'none'
                  : '0px',
                position: undefined,
              }}
              aria-hidden={!isOpen}
            >
              <div className='acc-panel-inner'>{renderPanelContent(item.content)}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/** Same as `<Accordion type='collapsible' />` — any number of panels may be open. */
export const Collapsible: React.FC<Omit<AccordionProps, 'type'>> = (props) => (
  <Accordion {...props} type='collapsible' />
);

export default Accordion;
